"use client";
import { IconCircleCheckFilled } from "@tabler/icons-react";
import { cn } from "@/lib/utils";
import { plans } from "@/constants/tier";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Button } from "./button";
import { useRouter } from "next/navigation";

interface StripePrices {
  monthly: {
    priceId: string;
    formatted: string;
    interval: string;
  };
  yearly: {
    priceId: string;
    formatted: string;
    interval: string;
  };
}

export function Pricing() {
  const [active, setActive] = useState<"monthly" | "yearly">("monthly");
  const [prices, setPrices] = useState<StripePrices | null>(null);
  const [loading, setLoading] = useState(true);
  const router = useRouter();

  const selectedPlan = plans.find((p) => p.id === active)!;

  // Fetch prices from local API (which fetches from Stripe)
  useEffect(() => {
    fetch("/api/stripe/prices")
      .then((res) => res.json())
      .then((data) => {
        if (!data.error) {
          setPrices(data);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to fetch prices:", err);
        setLoading(false);
      });
  }, []);

  const handleBuyClick = () => {
    // Redirect to signup with selected plan
    router.push(`/signup?plan=${active}`);
  };

  // Get dynamic price from Stripe or fallback to static
  const getDisplayPrice = () => {
    if (loading) return null;
    if (prices) {
      const stripePrice = active === "monthly" ? prices.monthly : prices.yearly;
      return `${stripePrice.formatted}/${stripePrice.interval}`;
    }
    return selectedPlan.price;
  };

  const displayPrice = getDisplayPrice();

  return (
    <div className="relative">
      {/* Monthly/Yearly Toggle */}
      <div className="flex items-center justify-center bg-neutral-100 dark:bg-neutral-800 w-fit mx-auto mb-12 rounded-full p-1">
        {plans.map((plan) => (
          <button
            key={plan.id}
            className={cn(
              "text-sm font-medium px-6 py-2.5 rounded-full relative transition-colors",
              active === plan.id
                ? "text-white dark:text-black"
                : "text-gray-500 dark:text-muted-dark hover:text-gray-700"
            )}
            onClick={() => setActive(plan.id)}
          >
            {active === plan.id && (
              <motion.span
                layoutId="pricing-toggle"
                transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
                className="absolute inset-0 bg-black dark:bg-white rounded-full"
              />
            )}
            <span className="relative z-10 flex items-center gap-2">
              {plan.name}
              {plan.badge && (
                <span className="text-xs bg-green-500 text-white px-2 py-0.5 rounded-full">
                  {plan.badge}
                </span>
              )}
            </span>
          </button>
        ))}
      </div>

      {/* Pricing Card */}
      <div className="max-w-lg mx-auto">
        <motion.div
          key={active}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="bg-[radial-gradient(164.75%_100%_at_50%_0%,#334155_0%,#0F172A_48.73%)] rounded-2xl p-8 shadow-2xl"
        >
          {/* Price */}
          <div className="text-center mb-8">
            <motion.div
              key={displayPrice}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3 }}
              className="mb-2"
            >
              {loading ? (
                <span className="inline-block w-32 h-12 bg-neutral-700 rounded animate-pulse" />
              ) : (
                <span className="text-5xl font-bold text-white">
                  {displayPrice}
                </span>
              )}
            </motion.div>
            <p className="text-neutral-400">{selectedPlan.description}</p>
            {selectedPlan.savings && (
              <p className="text-green-400 text-sm font-medium mt-2">
                {selectedPlan.savings}
              </p>
            )}
          </div>

          {/* Features */}
          <ul className="space-y-4 mb-8">
            {selectedPlan.features.map((feature) => (
              <li key={feature} className="flex items-center gap-3 text-neutral-300">
                <IconCircleCheckFilled className="h-5 w-5 text-white flex-shrink-0" />
                {feature}
              </li>
            ))}
          </ul>

          {/* CTA Button */}
          <Button
            onClick={handleBuyClick}
            className="w-full bg-white text-black hover:bg-white/90 rounded-full py-3 text-base font-semibold"
          >
            Start Free Trial
          </Button>

          {/* Trial Note */}
          <p className="text-center text-neutral-400 text-sm mt-4">
            7-day free trial included
          </p>
        </motion.div>
      </div>
    </div>
  );
}
